// RegisterUser.jsx
import BuildersLogo from "@components/BuildersLogo";
import HeadlessButton from "@components/Button";
import LanguageToggle from "@components/constant_components/LanguageToggle";
import FormikInput from "@components/form/FormikInput";
import Spinner from "@components/Spinner";
// import axios from "axios";
import axiosClient from "@service/axiosClient";

import { Field, Form, Formik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";

export default function RegistrationUser() {
  const { encryptedData } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const formikRef = useRef(null);
  const [initialValues, setInitialValues] = useState({
    first_name: "",
    second_name: "",
    third_name: "",
    fourth_name: "",
    email: "",
    contractor_id: "",
    password: "",
    confirm_password: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function verifyAndFetchWorkerData() {
      if (!encryptedData) {
        setError("No verification code provided");
        setIsLoading(false);
        return;
      }

      try {
        const response = await axiosClient.get(
          `/worker_api/worker/verify/registration/${encryptedData}`
        );

        if (response.data.status === "success") {
          setInitialValues({
            first_name: response.data.first_name || "",
            second_name: response.data.second_name || "",
            third_name: response.data.third_name || "",
            fourth_name: response.data.fourth_name || "",
            email: response.data.email || "",
            contractor_id: response.data.contractor_id || "",
            password: "",
            confirm_password: "",
          });
        } else {
          setError(response.data.error || "Invalid verification code");
        }
      } catch (err) {
        console.error("Verification failed:", err);
        setError(
          err?.response?.data?.error ||
            "Failed to verify registration link. Please contact support."
        );
      } finally {
        setIsLoading(false);
      }
    }

    verifyAndFetchWorkerData();
  }, [encryptedData]);

  useEffect(() => {
    formikRef.current?.validateForm();
  }, [t]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        password: Yup.string().required(t("register.validation.password")),
        confirm_password: Yup.string()
          .oneOf(
            [Yup.ref("password"), null],
            t("register.validation.passwordMatch")
          )
          .required(t("register.validation.confirmPassword")),
      }),
    [t]
  );

  async function onSubmit(values, { setSubmitting, setErrors }) {
    try {
      // 1️ Create the user
      const {
        data: { user_id, contractor_id },
      } = await axiosClient.post("/users/user_registration", values);
      if (!user_id || !contractor_id) {
        throw new Error("Missing user_id or contractor_id in response");
      }

      // 2️ Ask the backend to confirm registration
      await axiosClient.post("/users/finalize_registration_new", {
        user_id,
        contractor_id,
      });

      // 3️ Store only the form values (we’ll pull out email later)
      sessionStorage.setItem("pending_user_data", JSON.stringify(values));

      // 4️ Navigate to the code-entry page
      navigate(`/login/user`);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Could not send confirmation code. Please try again.";
      setErrors({ email: msg });
    } finally {
      setSubmitting(false);
    }
  }

  if (isLoading) {
    return (
      <div
        className="tw-form-container"
        style={{ backgroundImage: "url('/images/login-bg.png')" }}
      >
        <div className="tw-form-overlay" />
        <div className="tw-form-wrapper">
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4">
            <Spinner />
            <p className="tw-text-white">
              {t("register.verifying") || "Verifying registration link..."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="tw-form-container"
        style={{ backgroundImage: "url('/images/login-bg.png')" }}
      >
        <div className="tw-form-overlay" />
        <div className="tw-form-wrapper">
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
            <div className="tw-flex tw-justify-center tw-mb-4">
              <BuildersLogo />
            </div>
            <div className="tw-text-red-500 tw-text-center">
              <h2 className="tw-text-xl tw-font-bold tw-mb-2">
                {t("register.error") || "Error"}
              </h2>
              <p>{error}</p>
            </div>
            <HeadlessButton
              onClick={() => navigate("/landing")}
              className="tw-form-button"
            >
              {t("register.backToHome") || "Back to Home"}
            </HeadlessButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="tw-form-container"
      style={{ backgroundImage: "url('/images/login-bg.png')" }}
    >
      <div className="tw-form-language-toggle">
        <LanguageToggle />
      </div>
      <div className="tw-form-overlay" />
      <div className="tw-form-wrapper">
        <div className="tw-flex tw-justify-center tw-mb-4">
          <BuildersLogo />
        </div>

        <h2 className="tw-form-heading">{t("register.title")}</h2>

        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize={true}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form className="tw-space-y-4">
              <Field
                name="first_name"
                label={t("register.firstName")}
                as={FormikInput}
                error={touched.first_name && errors.first_name}
                disabled={true}
              />
              <Field
                name="second_name"
                label={t("register.secondName")}
                as={FormikInput}
                disabled={true}
              />
              <Field
                name="third_name"
                label={t("register.lastName")}
                as={FormikInput}
                error={touched.third_name && errors.third_name}
                disabled={true}
              />
              <Field
                name="fourth_name"
                label={t("register.secondLastName")}
                as={FormikInput}
                disabled={true}
              />
              <Field
                name="password"
                label={t("register.password")}
                type="password"
                as={FormikInput}
                error={touched.password && errors.password}
              />
              <Field
                name="confirm_password"
                label={t("register.confirmPassword")}
                type="password"
                as={FormikInput}
                error={touched.confirm_password && errors.confirm_password}
              />

              <HeadlessButton
                type="submit"
                disabled={isSubmitting}
                className="tw-form-button"
              >
                {isSubmitting ? (
                  <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                    {t("register.processing")}
                    <Spinner />
                  </div>
                ) : (
                  t("register.button")
                )}
              </HeadlessButton>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}
